import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Bot, Calculator, Check, ClipboardCheck, MessagesSquare, Play, Shield, Table, User } from 'lucide-react';
import { PAGES, usePageMeta } from '../seo/seo';

const PLATFORM_FEATURES = [
  'Custom Web based Task Panes',
  'Rich Custom Data Types',
  'Smart Automation & Custom Functions',
  'Natural language data access from Web & DB',
];

const SOLUTIONS = [
  {
    icon: <User />,
    title: 'Enriched Data for Smarter Decisions',
    description: 'Convert raw data into structured insights for better analysis & modeling.',
    features: [
      'Transform Data into Rich Data Types',
      'Improve Risk & Performance Modeling',
      'Seamless Integration with Financial Systems',
      'Make Data More Actionable',
    ],
  },
  {
    icon: <Calculator />,
    title: 'Smarter Financial Analytics',
    description: 'Leverage AI to blend private & public data for deeper financial insights.',
    features: [
      'AI-Powered Data Processing',
      'Enhanced Forecasting & Market Analysis',
      'Dynamic Data Blending with RAG',
      'Faster & More Accurate Reporting',
    ],
  },
  {
    icon: <Shield />,
    title: 'AI-Powered Compliance',
    description: 'Ensure accurate, audit-ready reports with automated regulatory & ESG reporting.',
    features: [
      'Auto-Generate Compliance Reports',
      'Reduce Risk of Manual Errors',
      'Meet ESG & Regulatory Standards',
      'Audit Trails & Secure Documentation',
    ],
  },
  {
    icon: <Bot />,
    title: 'AI Agents for Automation',
    description: 'Eliminate repetitive tasks with AI-driven workflow automation for finance teams.',
    features: [
      'Automate Data Validation & Risk Analysis',
      'Reduce Manual Work & Errors',
      'Smart Decision-Making AI Models',
      'Integrated with Your Existing Workflows',
    ],
  },
  {
    icon: <ClipboardCheck />,
    title: 'EOD & Production Monitoring',
    description: 'Prevent failures before they happen with real-time AI-powered monitoring.',
    features: [
      'Detect & Fix Anomalies Early',
      'Reduce EOD & Overnight Failures',
      'Real-Time Alerts via Slack, Email',
      'Proactive Incident Resolution',
    ],
  },
];

const SolutionsPage = () => {
  const navigate = useNavigate();
  usePageMeta(PAGES.solutions);

  const handleDemoButtonClick = () => {
    navigate('/webaiexcel');
  };

  const handleWatchDemosClick = () => {
    navigate('/webaiexcel#demos');
  };

  const handleContactButtonClick = () => {
    navigate('/contact');
  };

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Hero */}
      <section className="bg-gradient-to-r from-brandPurple to-brandBlue text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-24 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-white/80 mb-4">Our Solution</p>
          <h1 className="text-4xl md:text-5xl font-bold max-w-4xl mx-auto leading-tight">
            The future of finance isn’t about replacing Excel
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            It’s about making Excel smarter. WebAIExcel adds AI, live data and automation to the spreadsheets your teams already use.
          </p>
        </div>
      </section>

      {/* Platform */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center bg-gray-50 dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 p-8 md:p-12">
            <div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-brandPurple to-brandBlue text-white shadow-md mb-6">
                <Table size={28} />
              </div>
              <p className="text-sm font-semibold uppercase tracking-widest text-[#36AC98] mb-2">The Platform</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">WebAIExcel – Excel Elevated</h2>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
                Take Excel to a whole new level with all-in-one solutions that enhance your productivity.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={handleWatchDemosClick}
                  className="flex items-center gap-2 px-6 py-3 bg-brandPurple text-white font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300"
                >
                  <Play className="w-5 h-5" /> Watch the demos
                </button>
                <button
                  type="button"
                  onClick={handleContactButtonClick}
                  className="flex items-center gap-2 px-6 py-3 border border-[#3399ff] text-[#3399ff] dark:text-white dark:border-white font-semibold rounded-lg hover:bg-[#3399FF] hover:text-white dark:hover:bg-white/10 transition-colors"
                >
                  Talk to us <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2">
              {PLATFORM_FEATURES.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-3 bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-700"
                >
                  <span className="flex-shrink-0 mt-0.5 flex items-center justify-center w-6 h-6 rounded-full bg-[#36AC98]/15 text-[#36AC98]">
                    <Check className="w-4 h-4" />
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-100">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3399FF] dark:text-white">Solutions Built on WebAIExcel</h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Targeted solutions for the workflows that slow finance teams down.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {SOLUTIONS.map((solution) => (
              <SolutionCard key={solution.title} {...solution} />
            ))}

            {/* Fills the last grid slot with a prompt for anything not listed */}
            <div className="flex flex-col justify-center p-8 rounded-2xl bg-gradient-to-br from-brandPurple to-brandBlue text-white shadow-lg">
              <MessagesSquare size={32} className="mb-6 text-white/90" />
              <h3 className="text-xl font-semibold mb-3">Have a different workflow in mind?</h3>
              <p className="text-white/90 mb-8">
                Every team works differently. Tell us where your Excel process slows down and we’ll shape a solution around it.
              </p>
              <Link
                to="/contact"
                className="self-start inline-flex items-center gap-2 px-6 py-3 bg-white text-brandPurple font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300"
              >
                Let’s talk <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Call to action */}
      <section className="py-16 bg-[#3399ff]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Transform Your Team?</h2>
          <p className="text-lg text-white mb-8 max-w-3xl mx-auto">
            Explore WebAIExcel, a next-generation AI-powered Excel add-in designed to bridge the gap between Excel and modern web technologies.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={handleDemoButtonClick}
              className="px-8 py-3 bg-white text-[#3399FF] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300"
            >
              Get Started
            </button>
            <button
              type="button"
              onClick={handleContactButtonClick}
              className="px-8 py-3 border border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

interface SolutionCardProps {
  icon: React.ReactElement<{ size?: number }>;
  title: string;
  description: string;
  features: string[];
}

const SolutionCard: React.FC<SolutionCardProps> = ({ icon, title, description, features }) => {
  return (
    <div className="flex flex-col bg-white dark:bg-gray-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-[#36AC98] transition-all duration-300">
      <div className="inline-flex self-start p-3 rounded-xl bg-[#3399FF]/10 text-[#3399FF] dark:text-[#36AC98] mb-6">
        {React.cloneElement(icon, { size: 28 })}
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300 mb-6">{description}</p>
      <ul className="space-y-3 pt-6 border-t border-gray-100 dark:border-gray-700">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
            <Check className="w-5 h-5 flex-shrink-0 text-[#36AC98]" />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SolutionsPage;
